/**
 * VoicemailData.js
 * Talkroute
 *
 * Created by Vel Genov on 2/14/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

/**
 * A data class responsible for storing a single voicemail mailbox
 *
 * @param id the id of the voicemail mailbox
 * @param messageCount the number of messages a voicemail mailbox contains
 * @param description the text description of the mailbox
 * @param editable whether the user has permission to edit vmbox content
 */
export class VoicemailMailbox {
  public id: number;
  public messageCount: number;
  public description: string;
  public editable: boolean;

  constructor(opts) {
    this.id = opts.id.toString();
    this.messageCount = opts.messageCount;
    this.description = opts.description;
    this.editable = opts.editable;
  }
}

/**
 *  A static method to find a mailbox with the given id an return it's description. Return mailboxId if not found
 *
 * @param {*} mailboxId the id of the mailbox we are looking for
 * @param {*} mailboxes the array of mailboxes to look into
 */
export const getDescriptionForId = (mailboxId, mailboxes) => {
  if (Array.isArray(mailboxes) && mailboxes.length > 0) {
    // Returns the element if it finds it. Unidentified otherwise
    const mailbox = mailboxes.find((entry) => {
      const { id } = entry;
      return mailboxId === id;
    });

    if (mailbox) {
      return mailbox.description;
    }
  }
  return mailboxId;
};

/**
 *  A static method to find a mailbox with the given id and return its editable property. Return false if not found
 *
 * @param {*} mailboxId the id of the mailbox we are looking for
 * @param {*} mailboxes the array of mailboxes to look into
 */
export const getEditableForId = (mailboxId, mailboxes) => {
  
  if (mailboxId === 'Welcome to Talkroute') {
    return true;
  }

  if (Array.isArray(mailboxes) && mailboxes.length > 0) {
    // Returns the element if it finds it. Unidentified otherwise
    const mailbox = mailboxes.find((entry) => {
      const { id } = entry;
      return mailboxId === id;
    });

    if (mailbox) {
      return mailbox.editable;
    }
  }
  return false;
};

/**
 * Parses the incoming JSON voicemailMailbox data. Creates a VoicemailMailbox object for each line item
 *
 * @param {*} voicemailMailboxData an array containig voicemailMailbox data.
 */
export const voicemailMailboxJSONParser = (voicemailMailboxData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (Array.isArray(voicemailMailboxData) && voicemailMailboxData.length > 0) {
    // Process each entry
    const parsedData = voicemailMailboxData.map((entry) => {
      const {
        id,
        message_count: messageCount,
        description,
        editable,
      } = entry;

      const voicemailMailboxEntry = new VoicemailMailbox({
        id,
        messageCount,
        description,
        editable,
      });

      return voicemailMailboxEntry;
    });

    return parsedData;
  }
};

/**
 * Parses the incoming generic objects into VoicemailMailbox objects.
 * Checks for empty objects passed in
 *
 * @param {*} voicemailMailboxData an array containig voicemailMailbox data.
 */
export const voicemailBoxesRehydrator = (voicemailMailboxData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (Array.isArray(voicemailMailboxData) && voicemailMailboxData.length > 0) {
    // Process each entry
    const parsedData = voicemailMailboxData.map((entry) =>
      voicemailBoxesRehydrateMailbox(entry)
    );

    return parsedData;
  }
  return [];
};

/**
 * Parses the incoming single generic object into VoicemailMailbox.
 * Checks for an empty object being passed in.
 *
 * @param {*} voicemailMailbox a single generic object containing a voicemail.
 */
export const voicemailBoxesRehydrateMailbox = (voicemailMailbox) => {
  if (voicemailMailbox) {
    const { id, messageCount, description, editable } = voicemailMailbox;

    const voicemaiBoxEntry = new VoicemailMailbox({
      id,
      messageCount,
      description,
      editable,
    });

    return voicemaiBoxEntry;
  }
  return voicemailMailbox;
};
